function toDateString(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString();
}

function buildStatusLabel(item) {
  switch (item.status) {
    case 'completed':
      return 'Ready';
    case 'failed':
      return 'Failed';
    case 'processing':
      return 'Processing';
    default:
      return 'Queued';
  }
}

const ReportExports = {
  items: [],
  listEl: null,
  badgeEl: null,
  toggleEl: null,
  spinnerEl: null,
  initialized: false,
  handleActionClickBound: null,
  handleDeleteClickBound: null,
  handleDropdownShownBound: null,
  handleDropdownHiddenBound: null,
  pollIntervalId: null,
  pollIntervalMs: 10000,
  fetchInFlight: false,
  fetchQueued: false,

  init() {
    this.listEl = document.getElementById('report-exports-list');
    this.badgeEl = document.getElementById('report-exports-badge');
    this.toggleEl = document.getElementById('reportExportsDropdown');
    this.spinnerEl = document.getElementById('report-exports-spinner');
    this.handleActionClickBound = this.handleActionClick.bind(this);
    this.handleDeleteClickBound = this.handleDeleteClick.bind(this);
    this.handleDropdownShownBound = this.handleDropdownShown.bind(this);
    this.handleDropdownHiddenBound = this.handleDropdownHidden.bind(this);
    this.initialized = true;

    if (!this.listEl) {
      return;
    }

    this.attachDropdownListeners();
    this.updatePollingState();
    this.fetch();
  },

  fetch() {
    if (this.fetchInFlight) {
      this.fetchQueued = true;
      return;
    }

    this.fetchInFlight = true;
    fetch('/report_exports.json', { headers: { Accept: 'application/json', 'X-Session-Passive': '1' }, credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to load exports');
        }
        return response.json();
      })
      .then((data) => {
        if (!Array.isArray(data)) {
          return;
        }
        this.items = data;
        this.render();
        this.updatePollingState();
      })
      .catch(() => {
        // Silent fail – dropdown remains empty
        this.updatePollingState();
      })
      .finally(() => {
        this.fetchInFlight = false;
        if (this.fetchQueued) {
          this.fetchQueued = false;
          this.fetch();
        }
      });
  },

  enqueue(trigger) {
    if (!trigger) {
      return;
    }

    const url = trigger.dataset.url;
    if (!url) {
      return;
    }

    const form = trigger.closest('form');
    if (!form) {
      return;
    }

    const icon = trigger.querySelector('i');
    const originalIconClass = icon ? icon.className : null;

    trigger.dataset.loading = 'true';
    trigger.classList.add('disabled');
    trigger.setAttribute('aria-busy', 'true');
    if (icon) {
      icon.className = 'bi bi-hourglass-split';
    }

    this.showDropdown();

    if (this.spinnerEl) {
      this.spinnerEl.classList.remove('d-none');
    }

    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value === null || value === undefined) {
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((entry) => params.append(`${key}[]`, entry));
      } else {
        params.append(key, value);
      }
    });

    params.set('performance', 'false');

    if (trigger.dataset.sourceUrl) {
      params.append('source_url', trigger.dataset.sourceUrl);
    }

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: params
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Request failed');
        }
        return response.json();
      })
      .then((data) => {
        this.upsert(data);
        this.showDropdown();
      })
      .catch(() => {
        trigger.classList.remove('disabled');
      })
      .finally(() => {
        trigger.classList.remove('disabled');
        trigger.dataset.loading = 'false';
        trigger.removeAttribute('aria-busy');
        if (icon && originalIconClass) {
          icon.className = originalIconClass;
        }
      });
  },

  upsert(item) {
    if (!item || typeof item.id === 'undefined') {
      return;
    }

    const index = this.items.findIndex((existing) => existing.id === item.id);
    const previousStatus = index >= 0 ? this.items[index].status : undefined;

    if (index >= 0) {
      this.items[index] = { ...this.items[index], ...item };
    } else {
      this.items.unshift(item);
    }
    this.updatePollingState();

    this.render();

    if (item.status === 'completed' && previousStatus !== 'completed') {
      this.showDropdown();
    }
  },

  render() {
    if (!this.listEl) {
      return;
    }

    this.attachCloseListener();

    this.listEl.innerHTML = '';

    if (!this.items.length) {
      const emptyEl = document.createElement('div');
      emptyEl.className = 'px-3 py-3 text-muted small';
      emptyEl.textContent = 'Generated PDFs appear here.';
      this.listEl.appendChild(emptyEl);
      this.updateBadge();
      this.attachCloseListener();
      return;
    }

    const displayItems = this.items.slice(0, 5);

    displayItems.forEach((item, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'px-3 py-3 small';
      if (index !== displayItems.length - 1) {
        wrapper.className += ' border-bottom';
      }

      const headerRow = document.createElement('div');
      headerRow.className = 'd-flex justify-content-between align-items-start gap-2';

      const titleWrap = document.createElement('div');
      titleWrap.className = 'd-flex align-items-center gap-2 flex-wrap';

      const titleLink = document.createElement('a');
      titleLink.className = 'fw-semibold text-decoration-none';
      titleLink.textContent = item.title || 'Download';
      titleLink.href = '#pdfModal';
      titleLink.dataset.reportExportAction = 'preview';
      titleLink.dataset.reportExportId = item.id;
      if (item.status === 'completed' && item.preview_path) {
        titleLink.dataset.bsToggle = 'modal';
        titleLink.dataset.bsTarget = '#pdfModal';
        titleLink.dataset.bsUrl = item.preview_path;
        titleLink.setAttribute('data-bs-toggle', 'modal');
        titleLink.setAttribute('data-bs-target', '#pdfModal');
        titleLink.setAttribute('data-bs-url', item.preview_path);
      } else {
        titleLink.classList.add('disabled');
        titleLink.setAttribute('aria-disabled', 'true');
        titleLink.addEventListener('click', (e) => e.preventDefault());
      }
      titleWrap.appendChild(titleLink);

      const downloadLink = document.createElement('a');
      downloadLink.dataset.reportExportId = item.id;
      downloadLink.dataset.reportExportAction = 'download';
      downloadLink.className = 'text-primary';
      downloadLink.innerHTML = '<i class="bi bi-download"></i>';
      if (item.status === 'completed' && item.download_path) {
        downloadLink.href = item.download_path;
        downloadLink.target = '_blank';
        downloadLink.rel = 'noopener';
      } else {
        downloadLink.href = '#';
        downloadLink.classList.add('disabled');
        downloadLink.setAttribute('aria-disabled', 'true');
        downloadLink.addEventListener('click', (e) => e.preventDefault());
      }
      titleWrap.appendChild(downloadLink);

      if (item.status === 'completed' && !item.read) {
        const unreadDot = document.createElement('span');
        unreadDot.className = 'd-inline-block bg-danger rounded-circle flex-shrink-0';
        unreadDot.style.width = '0.5rem';
        unreadDot.style.height = '0.5rem';
        unreadDot.title = 'New';
        unreadDot.dataset.unreadDot = item.id;
        titleWrap.appendChild(unreadDot);
      }

      headerRow.appendChild(titleWrap);

      const deleteLink = document.createElement('a');
      deleteLink.href = '#';
      deleteLink.className = 'text-danger';
      deleteLink.innerHTML = '<i class="bi bi-x"></i>';
      deleteLink.dataset.behavior = 'delete-report-export';
      deleteLink.dataset.deleteUrl = item.destroy_path;
      deleteLink.dataset.reportExportId = item.id;
      headerRow.appendChild(deleteLink);

      wrapper.appendChild(headerRow);

      const metaRow = document.createElement('div');
      metaRow.className = 'd-flex justify-content-between align-items-center text-muted mt-2';
      const statusLabel = document.createElement('span');
      statusLabel.textContent = buildStatusLabel(item);
      const timestamp = document.createElement('span');
      timestamp.className = 'ms-2';
      timestamp.textContent = toDateString(item.updated_at || item.created_at);
      metaRow.appendChild(statusLabel);
      metaRow.appendChild(timestamp);
      wrapper.appendChild(metaRow);

      if (item.status === 'failed') {
        const errorEl = document.createElement('div');
        errorEl.className = 'mt-2 text-danger';
        errorEl.textContent = item.error_message || 'Something went wrong.';
        wrapper.appendChild(errorEl);
      } else if (item.status !== 'completed') {
        const progressWrapper = document.createElement('div');
        progressWrapper.className = 'progress mt-2';

        const progressValue = Math.max(5, Math.min(100, item.progress || 0));
        const progressBar = document.createElement('div');
        progressBar.className = 'progress-bar progress-bar-striped';
        if (item.status === 'processing') {
          progressBar.classList.add('progress-bar-animated');
        }
        progressBar.setAttribute('role', 'progressbar');
        progressBar.setAttribute('aria-valuemin', '0');
        progressBar.setAttribute('aria-valuemax', '100');
        progressBar.setAttribute('aria-valuenow', progressValue.toString());
        progressBar.style.width = `${progressValue}%`;

        progressWrapper.appendChild(progressBar);
        wrapper.appendChild(progressWrapper);
      }

      this.listEl.appendChild(wrapper);
    });

    if (this.items.length > 5) {
      const footer = document.createElement('div');
      footer.className = 'px-3 py-2 bg-light border-top text-center';
      const viewAllLink = document.createElement('a');
      viewAllLink.href = '/report_exports';
      viewAllLink.className = 'small fw-semibold';
      viewAllLink.textContent = 'View all exports';
      footer.appendChild(viewAllLink);
      this.listEl.appendChild(footer);
    }

    this.updateBadge();
    this.attachActionListeners();
    this.attachDeleteListeners();
    this.updatePollingState();
  },

  updateBadge() {
    const pending = this.items.some((item) => item.status !== 'completed' && item.status !== 'failed');
    const unreadCount = this.items.filter((item) => item.status === 'completed' && !item.read).length;

    if (this.spinnerEl) {
      if (pending) {
        this.spinnerEl.classList.remove('d-none');
      } else {
        this.spinnerEl.classList.add('d-none');
      }
    }

    if (this.badgeEl) {
      if (unreadCount > 0) {
        this.badgeEl.textContent = unreadCount;
        this.badgeEl.classList.remove('d-none');
      } else {
        this.badgeEl.textContent = '';
        this.badgeEl.classList.add('d-none');
      }
    }
  },

  attachActionListeners() {
    if (!this.listEl || !this.handleActionClickBound) {
      return;
    }

    const actionButtons = this.listEl.querySelectorAll('[data-report-export-action]');
    actionButtons.forEach((button) => {
      if (button.dataset.reportExportListenerAttached) {
        return;
      }
      button.addEventListener('click', this.handleActionClickBound);
      button.dataset.reportExportListenerAttached = 'true';
    });

  },

  attachDeleteListeners() {
    if (!this.listEl || !this.handleDeleteClickBound) {
      return;
    }

    const deleteLinks = this.listEl.querySelectorAll('[data-behavior="delete-report-export"]');
    deleteLinks.forEach((link) => {
      if (link.dataset.reportExportDeleteListenerAttached) {
        return;
      }
      link.addEventListener('click', this.handleDeleteClickBound);
      link.dataset.reportExportDeleteListenerAttached = 'true';
    });
  },

  attachCloseListener() {
    const closeLink = document.querySelector('[data-report-export-action="close-dropdown"]');
    if (closeLink && !closeLink.dataset.reportExportListenerAttached) {
      closeLink.addEventListener('click', (event) => {
        event.preventDefault();
        const dropdownApi = window.bootstrap?.Dropdown?.getOrCreateInstance(this.toggleEl);
        dropdownApi?.hide();
      });
      closeLink.dataset.reportExportListenerAttached = 'true';
    }
  },

  attachDropdownListeners() {
    if (!this.toggleEl || !this.handleDropdownShownBound || !this.handleDropdownHiddenBound) {
      return;
    }

    if (this.toggleEl.dataset.reportExportDropdownListenerAttached === 'true') {
      return;
    }

    this.toggleEl.addEventListener('shown.bs.dropdown', this.handleDropdownShownBound);
    this.toggleEl.addEventListener('hidden.bs.dropdown', this.handleDropdownHiddenBound);
    this.toggleEl.dataset.reportExportDropdownListenerAttached = 'true';
  },

  handleDropdownShown() {
    this.fetch();
  },

  handleDropdownHidden() {
    this.updatePollingState();
  },

  handleActionClick(event) {
    const button = event.currentTarget;
    if (!button) {
      return;
    }

    const action = button.dataset.reportExportAction;
    const reportExportId = button.dataset.reportExportId;
    if (!reportExportId) {
      return;
    }

    if (button.classList.contains('disabled') || button.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    if (action === 'preview' || action === 'download') {
      this.markAsRead(reportExportId);
    }
  },

  handleDeleteClick(event) {
    event.preventDefault();
    const deleteLink = event.currentTarget;
    if (!deleteLink) {
      return;
    }

    const url = deleteLink.dataset.deleteUrl;
    const reportExportId = deleteLink.dataset.reportExportId;
    if (!url || !reportExportId) {
      return;
    }

    deleteLink.classList.add('disabled');
    deleteLink.setAttribute('aria-disabled', 'true');

    fetch(url, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        Accept: 'application/json'
      },
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok && response.status !== 204) {
          throw new Error('Delete failed');
        }
        this.remove(reportExportId);
      })
      .catch(() => {
        deleteLink.classList.remove('disabled');
        deleteLink.removeAttribute('aria-disabled');
      })
      .finally(() => {
        if (deleteLink.isConnected) {
          deleteLink.classList.remove('disabled');
          deleteLink.removeAttribute('aria-disabled');
        }
      });
  },

  showDropdown() {
    if (!this.toggleEl) {
      return;
    }

    const dropdownApi = window.bootstrap?.Dropdown?.getOrCreateInstance(this.toggleEl);
    if (dropdownApi && typeof dropdownApi.show === 'function') {
      dropdownApi.show();
    }
  },

  remove(reportExportId) {
    this.items = this.items.filter((item) => String(item.id) !== String(reportExportId));
    this.render();
  },

  markAsRead(reportExportId) {
    const item = this.items.find((existing) => String(existing.id) === String(reportExportId));
    if (!item || item.read || item.status !== 'completed') {
      return;
    }

    const originalRead = item.read;
    item.read = true;
    this.render();

    fetch(`/report_exports/${reportExportId}/mark_read`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify({})
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Mark read failed');
        }
        return response.json();
      })
      .then((data) => {
        this.upsert(data);
      })
      .catch(() => {
        item.read = originalRead;
        this.render();
      });
  },

  hasPending() {
    return this.items.some((existing) => existing.status !== 'completed' && existing.status !== 'failed');
  },

  startPolling() {
    if (this.pollIntervalId) {
      return;
    }

    if (!this.listEl && !this.toggleEl) {
      return;
    }

    this.pollIntervalId = window.setInterval(() => {
      this.fetch();
    }, this.pollIntervalMs);
  },

  stopPolling() {
    if (!this.pollIntervalId) {
      return;
    }

    window.clearInterval(this.pollIntervalId);
    this.pollIntervalId = null;
  },

  updatePollingState() {
    if (!this.initialized) {
      return;
    }

    if (this.hasPending()) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }
};

window.ReportExports = ReportExports;

document.addEventListener('turbo:load', () => {
  ReportExports.init();
});

document.addEventListener('turbo:before-cache', () => {
  ReportExports.stopPolling();
});

document.addEventListener('click', (event) => {
  // Support any async export trigger following the convention:
  // <a data-behavior="enqueue-..." data-url="/path.json">...</a>
  const trigger = event.target.closest('[data-behavior^="enqueue-"][data-url]');
  if (!trigger) return;
  event.preventDefault();
  if (trigger.dataset.loading === 'true') return;
  ReportExports.enqueue(trigger);
});

export default ReportExports;
